import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  AccountCircle,
  Menu as MenuIcon,
  Phone,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export const Header: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    // { label: 'Services', path: '/services' }, // Hidden from navigation
    { label: 'Contact', path: '/contact' },
  ];

  const protectedMenuItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Expert Advisors', path: '/expert-advisors' },
    { label: 'Trading Community', path: '/community' },
    { label: 'Synthetics Signals', path: '/signals' },
  ];

  const renderMobileMenu = (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          width: 250,
        },
      }}
    >
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.label}
            onClick={() => {
              navigate(item.path);
              setMobileMenuOpen(false);
            }}
            sx={{ cursor: 'pointer' }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        {currentUser && protectedMenuItems.map((item) => (
          <ListItem
            key={item.label}
            onClick={() => {
              navigate(item.path);
              setMobileMenuOpen(false);
            }}
            sx={{ cursor: 'pointer' }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        {/* Mobile Login/Signup in Drawer */}
        {!currentUser && (
          <>
            <ListItem
              onClick={() => {
                navigate('/login');
                setMobileMenuOpen(false);
              }}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText primary="Login" />
            </ListItem>
            <ListItem
              onClick={() => {
                navigate('/signup');
                setMobileMenuOpen(false);
              }}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText primary="Sign Up" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  );

  const renderProfileMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={() => navigate('/profile')}>Profile</MenuItem>
      <MenuItem onClick={() => navigate('/settings')}>Settings</MenuItem>
      <MenuItem onClick={handleLogout}>Logout</MenuItem>
    </Menu>
  );

  return (
    <>
      <AppBar position="fixed" elevation={0}>
        <Toolbar sx={{ px: { xs: 1, sm: 2 }, minHeight: { xs: '56px', sm: '64px' } }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: { xs: 1, sm: 2 }, p: { xs: 1, sm: 1.5 } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box display="flex" alignItems="center" sx={{ flexGrow: isMobile ? 1 : 0 }}>
            {/* T1 FX Team Logo */}
            <Box
              component="img"
              src="/images/Logos/T1 Logo White Banner Transp2.png"
              alt="T1 FX Team Logo"
              sx={{
                height: { xs: 28, sm: 32, md: 40 },
                width: 'auto',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  filter: 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.3))',
                },
              }}
              onClick={() => navigate('/')}
            />
          </Box>

          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', ml: 4 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  sx={{ 
                    mx: 1, 
                    fontWeight: 500,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {currentUser && protectedMenuItems.map((item) => (
                <Button
                  key={item.label}
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  sx={{ 
                    mx: 1, 
                    fontWeight: 500,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Call Center Number - Mobile Optimized */}
          {!isSmallMobile && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mx: { xs: 0.5, sm: 1, md: 2 },
                color: 'primary.main',
                '&:hover': {
                  color: 'primary.light',
                },
                transition: 'color 0.3s ease',
              }}
            >
              <Phone sx={{ 
                mr: { xs: 0.5, sm: 1 }, 
                fontSize: { xs: '1rem', sm: '1.2rem' } 
              }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '1rem' },
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                0870 126 287
              </Typography>
            </Box>
          )}

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            {currentUser ? (
              <>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ 
                    mr: { xs: 1, sm: 2 }, 
                    display: { xs: 'none', sm: 'inline' },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    maxWidth: { xs: '80px', sm: 'none' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Welcome, {currentUser.displayName?.split(' ')[0] || 'User'}
                </Typography>
                <IconButton
                  size={isSmallMobile ? 'small' : 'large'}
                  edge="end"
                  aria-label="account of current user"
                  aria-controls="profile-menu"
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                    p: { xs: 0.5, sm: 1 },
                  }}
                >
                  <AccountCircle sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                </IconButton>
              </>
            ) : (
              !isMobile && (
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                  <Button
                    color="inherit"
                    onClick={() => navigate('/login')}
                    sx={{ 
                      '&:hover': {
                        color: 'primary.main',
                      },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1, sm: 2 },
                      minWidth: { xs: 'auto', sm: '64px' },
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/signup')}
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.5, sm: 1 },
                      minWidth: { xs: 'auto', sm: '64px' },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px rgba(255, 215, 0, 0.3)',
                      },
                    }}
                  >
                    Sign Up
                  </Button>
                </Box>
              )
            )}
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderProfileMenu}
    </>
  );
}; 